import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Clock, Tag, Search } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogHeader = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-2xl border-b border-white/[0.04]">
    <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <img
          src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg"
          alt="Disruptiva"
          className="h-8 w-auto"
        />
        <span className="font-space font-semibold text-white text-base tracking-wide hidden sm:block">DISRUPTIVA</span>
      </Link>
      <div className="flex items-center gap-6">
        <Link to="/blog" className="text-[13px] text-white/60 hover:text-white transition-colors font-space tracking-wide">Blog</Link>
        <Link to="/" className="btn-primary text-sm py-3 px-6" data-testid="blog-header-cta">
          Hablemos
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </nav>
  </header>
);

const CategoryPill = ({ name, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-space transition-all duration-300 ${
      active
        ? "bg-purple-600/20 border border-purple-500/40 text-purple-300"
        : "bg-white/[0.03] border border-white/[0.06] text-white/40 hover:text-white/60 hover:border-white/10"
    }`}
    data-testid={`category-${name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {name}
    <span className="text-xs opacity-50">{count}</span>
  </button>
);

const ArticleCard = ({ article, featured = false }) => {
  const date = new Date(article.published_date).toLocaleDateString("es-ES", {
    year: "numeric", month: "long", day: "numeric"
  });

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card overflow-hidden group ${featured ? "md:col-span-2 md:row-span-2" : ""}`}
      data-testid={`article-card-${article.slug}`}
    >
      <Link to={`/blog/${article.slug}`} className="block p-8 h-full">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-space text-purple-400/70 tracking-wide">{article.category}</span>
          <span className="text-white/10">|</span>
          <span className="text-xs text-white/25">{date}</span>
        </div>

        <h2 className={`font-space font-bold text-white mb-4 leading-tight group-hover:text-purple-300 transition-colors duration-300 ${
          featured ? "text-2xl md:text-3xl" : "text-lg"
        }`}>
          {article.title}
        </h2>

        <p className={`text-white/30 leading-relaxed mb-6 ${featured ? "text-base" : "text-sm"}`}>
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-xs text-white/25">
              <Clock className="w-3 h-3" />
              {article.read_time} min
            </span>
          </div>
          <span className="text-sm text-purple-400/60 group-hover:text-purple-400 transition-colors flex items-center gap-1">
            Leer <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Link>
    </motion.article>
  );
};

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";
  const searchQuery = searchParams.get("q") || "";
  const [search, setSearch] = useState(searchQuery);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory) params.set("category", activeCategory);
        params.set("limit", "50");

        const [articlesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/blog/articles?${params}`),
          axios.get(`${API}/blog/categories`),
        ]);
        setArticles(articlesRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchData();
  }, [activeCategory]);

  const filteredArticles = search
    ? articles.filter(a =>
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.excerpt.toLowerCase().includes(search.toLowerCase())
      )
    : articles;

  const featured = filteredArticles.filter(a => a.featured);
  const regular = filteredArticles.filter(a => !a.featured);

  const handleCategoryClick = (cat) => {
    const params = new URLSearchParams(searchParams);
    if (cat === activeCategory) {
      params.delete("category");
    } else {
      params.set("category", cat);
    }
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-black">
      <SEOHead
        title="Blog — Comunicación Estratégica, Crisis y Reputación"
        description="Artículos especializados sobre gestión de crisis, reputación corporativa, relaciones públicas y comunicación estratégica. Insights de Disruptiva."
        path="/blog"
      />
      <BlogHeader />

      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="pulse-dot" />
              <span className="text-[13px] text-white/30 font-space tracking-widest uppercase">Blog</span>
            </div>
            <h1 className="font-space font-bold text-4xl md:text-6xl text-white leading-tight mb-6" data-testid="blog-heading">
              Insights en<br />comunicación estratégica
            </h1>
            <p className="text-lg text-white/30 max-w-2xl leading-relaxed">
              Exploramos las mejores prácticas en gestión de crisis, reputación corporativa, relaciones públicas y comunicación estratégica para empresas de Centroamérica y Latinoamérica. Nuestro equipo de especialistas comparte análisis profundos, casos de estudio reales y metodologías probadas que ayudan a directivos, comunicadores y equipos de marketing a proteger su marca y amplificar su impacto.
            </p>
          </motion.div>

          {/* Search + categories */}
          <div className="mb-12 space-y-6">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar artículos..."
                className="input pl-11"
                data-testid="blog-search"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <CategoryPill
                name="Todos"
                count={articles.length}
                active={!activeCategory}
                onClick={() => handleCategoryClick("")}
              />
              {categories.map((cat) => (
                <CategoryPill
                  key={cat.name}
                  name={cat.name}
                  count={cat.count}
                  active={activeCategory === cat.name}
                  onClick={() => handleCategoryClick(cat.name)}
                />
              ))}
            </div>
          </div>

          {/* Articles grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="glass-card p-8 animate-pulse">
                  <div className="h-4 bg-white/5 rounded w-1/3 mb-4" />
                  <div className="h-6 bg-white/5 rounded w-full mb-2" />
                  <div className="h-6 bg-white/5 rounded w-2/3 mb-4" />
                  <div className="h-4 bg-white/5 rounded w-full mb-2" />
                  <div className="h-4 bg-white/5 rounded w-4/5" />
                </div>
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/30 text-lg">No se encontraron artículos.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
              {featured.map((article) => (
                <ArticleCard key={article.id} article={article} featured />
              ))}
              {regular.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg" alt="Disruptiva" className="h-7 w-auto opacity-40" />
            <span className="text-sm text-white/20">© 2026 Disruptiva</span>
          </Link>
          <Link to="/" className="text-sm text-white/30 hover:text-white/50 transition-colors">
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Volver al inicio
          </Link>
        </div>
      </footer>
    </div>
  );
}
